import { ChangeDetectorRef, Component, OnInit, ViewChild } from '@angular/core';
import { IntRegistersApiService } from '../../../../../../services/int-registers-api/int-registers-api.service';
import { IntRegistersDatasource } from '../../int-registers.datasource';
import { AlertToastService, InternationalInvApiService } from '@services';
import { TableComponent } from '@shared';
import {
  IIntInvoiceRegister,
  IIntInvoiceRegisterPrintPayload,
  IIntInvoiceRegisterRemovePayload,
} from '../../../../../../models/invoice/int-invoice-register';
import { DisplayedColumns } from '../../../registers.constants';
import { LocalStorageService } from 'ngx-webstorage';
import { MatDialog } from '@angular/material/dialog';
import { BehaviorSubject, finalize, take } from 'rxjs';
import { IApiError } from '@models';
import { InvoiceService } from '../../../../../../services/invoices/invoice.service';
import { IntRegisterShowComponent } from '../int-register-show/int-register-show.component';
import {
  EMENInvoicePrintForm, EMENInvoiceState, ZEBRA_ALLOWED_PRINT_STATES,
} from '../../../../../../components/dashboard/invoices/components/invoices-my/invoices-my-international/invoices-my-international.constants';
import { IInternationalInvoice } from '../../../../../../models/international-invoice';
import {
  InternationalRegisterRenameComponent,
} from '../../../international-register-rename/international-register-rename.component';

@Component({
  selector: 'app-int-registers-view',
  templateUrl: './int-registers-view.component.html',
  styleUrls: ['./int-registers-view.component.scss',
    '../../../../../../../styles/mat-override/mat-table.common.scss',
  ],
})
export class IntRegistersViewComponent
  extends TableComponent<IIntInvoiceRegister, IntRegistersDatasource>
  implements OnInit {
  dataSource: IntRegistersDatasource;
  disableColumns: string[] = [];
  displayedColumns = DisplayedColumns;
  storageKey = 'app-int-registers';
  subMenuLoading$ = new BehaviorSubject<boolean>(false);
  isPrintLoading$ = new BehaviorSubject<boolean>(false);
  expandedElement: IIntInvoiceRegister | null;
  invoices: IInternationalInvoice[] = [];
  protected readonly PRINT_FORMS = EMENInvoicePrintForm;
  @ViewChild(IntRegisterShowComponent, {
    static: false,
    read: IntRegisterShowComponent,
  }) private registerShowComponent: IntRegisterShowComponent;

  constructor(private intRegistersApi: IntRegistersApiService,
              private invoiceRegisterService: InternationalInvApiService,
              private cd: ChangeDetectorRef,
              private alertToastService: AlertToastService,
              private dialog: MatDialog,
              private invoiceService: InvoiceService,
              protected storage: LocalStorageService,
  ) {
    super();
  }

  get printInvoicesDisabled(): boolean {
    return this.invoices.length === 0 || this.subMenuLoading$.value;
  };

  get printMarkingDisabled(): boolean {
    return this.invoices.filter((el: IInternationalInvoice) => el.State === 'Verified').length === 0;
  }

  ngOnInit(): void {
    super.ngOnInit();
    this.initDataSource();
  }

  initDataSource(): void {
    this.dataSource = new IntRegistersDatasource(
      this.invoiceRegisterService,
      this.intRegistersApi,
    );

    this.dataSource.loadRegisters(0, this.itemsPerPage);
  }

  update(): void {
    this.dataSource.loadRegisters(this.paginator.pageIndex, this.itemsPerPage);
  }

  getInvoices(registerNumber: string): void {
    this.invoices = [];
    this.subMenuLoading$.next(true);
    this.intRegistersApi.getInvoices(registerNumber, null, null, 1)
      .pipe(finalize(() => this.subMenuLoading$.next(false)))
      .subscribe(
        (invoices) => {
          this.invoices = invoices.list;
          this.cd.detectChanges();
        },
        (errors) => {
          if (errors[0]) {
            this.alertToastService.pushApiErrors(errors);
          }
          this.invoices = [];
        },
      );
  }

  printRegister(register: IIntInvoiceRegister): void {
    this.isPrintLoading$.next(true);

    const data: IIntInvoiceRegisterPrintPayload = {
      ScanSheetRefs: [register.ref],
      Type: 'pdf',
      printForm: 'International_ScanSheet',
    };

    this.intRegistersApi.print(data)
      .pipe(finalize(() => this.isPrintLoading$.next(false)))
      .subscribe(() => {
        this.updateRegisterHeaderPrinted(register, '1');
      }, (errors) => {
        if (errors[0]) {
          this.alertToastService.pushApiErrors(errors);
          this.update();
        }
      });
  }

  printMarkingZebraHundred(): void {
    if (!this.invoices.length) {
      return;
    }

    this.isPrintLoading$.next(true);
    this.subMenuLoading$.next(true);

    const invoicesIds = this.invoices.filter((el: IInternationalInvoice) => el.State === 'Verified').map((el: IInternationalInvoice) => el.Ref);
    const printForm = this.PRINT_FORMS.MARKING_5;

    this.invoiceService
      .printDocuments(invoicesIds, 'pdf', {
        printForm,
      })
      .pipe(finalize(() => {
        this.subMenuLoading$.next(false);
        this.isPrintLoading$.next(false);
      }))
      .subscribe(
        () => {
          this.updateInvoicesAsPrinted();
        },
        (errors: IApiError[]) => {
          if (errors[0]) {
            this.alertToastService.pushApiErrors(errors);
          }
          this.updateInvoicesAsPrinted();
        },
      );
  }

  updateInvoicesAsPrinted(): void {
    if (!!this.expandedElement) {
      this.registerShowComponent?.updateInvoices();
    }
  }

  printInvoices(): void {
    this.isPrintLoading$.next(true);

    const invoicesIds = this.invoices.map((el: IInternationalInvoice) => el.Ref);
    const printForm = this.PRINT_FORMS.INTERNATIONAL_MARKING;

    this.invoiceService.printDocuments(invoicesIds, 'pdf', {
      printForm,
    })
      .pipe(finalize(() => this.isPrintLoading$.next(false)))
      .subscribe(
        () => {
          this.updateInvoicesAsPrinted();
        },
        (errors: IApiError[]) => {
          if (errors[0]) {
            this.alertToastService.pushApiErrors(errors);
          }
          this.updateInvoicesAsPrinted();
        },
      );
  }

  rename(register: IIntInvoiceRegister): void {
    const renameDialog = this.dialog.open(InternationalRegisterRenameComponent, {
      data: {
        register: {
          ref: register.ref,
          description: register.description,
        },
      },
      panelClass: 'no-padding',
      autoFocus: false,
      minWidth: '440px',
      disableClose: true,
    });

    renameDialog.afterClosed().pipe(take(1)).subscribe((result: { success?: boolean, description?: string }) => {
      if (result.success && result.description) {
        register.description = result.description;
        this.cd.detectChanges();
      }
    });
  }

  remove(ref: string): void {
    const payload: IIntInvoiceRegisterRemovePayload = {
      ScanSheetRefs: [ref],
    };

    this.intRegistersApi.delete(payload).subscribe((data) => {
      const res = data.data;
      if (res.ScanSheetRefs?.Errors?.length) {
        this.alertToastService.pushRegisterRemoveApiErrors(
          Array.from(res?.ScanSheetRefs?.Errors),
          'Помилка розформування',
        );
      } else {
        this.alertToastService.pushSuccess('Реєстр успішно розформовано!');
      }
      this.update();
    }, errors => {
      if (errors[0]) {
        this.alertToastService.pushApiErrors(errors);
      }
      this.update();
    });
  }

  onExpandRow(register: IIntInvoiceRegister): void {
    this.expandedElement = this.expandedElement === register ? null : register;
    this.cd.detectChanges();
  }

  updateRegisterHeaderCount(register: IIntInvoiceRegister, docNumbers: string[]): void {
    register.count = register.count - docNumbers.length;
  }

  updateRegisterHeaderPrinted(register: IIntInvoiceRegister, printed: string): void {
    register.printed = printed;
  }
}
